import { useState } from 'react'
import { useSignIn } from '@clerk/clerk-react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ArrowRight, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BrainLogo } from '@/components/ui/brain-logo'

export default function ForgotPasswordPage() {
  const { signIn, isLoaded } = useSignIn()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded || !signIn) return

    setIsLoading(true)
    setError(null)
    setSuccess(false)

    try {
      // Create the sign-in attempt with reset password strategy
      const result = await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      })
      
      // Find the reset_password_email_code factor to get the emailAddressId
      const resetFactor = result.supportedFirstFactors?.find(
        (factor) => factor.strategy === 'reset_password_email_code'
      ) as { strategy: string; emailAddressId?: string; email_address_id?: string } | undefined
      
      if (resetFactor) {
        // Get emailAddressId (camelCase) or email_address_id (snake_case) from the factor
        const emailAddressId = resetFactor.emailAddressId || (resetFactor as any).email_address_id
        
        if (emailAddressId) {
          // Prepare the reset to send the email with the required emailAddressId
          await signIn.prepareFirstFactor({
            strategy: 'reset_password_email_code',
            emailAddressId: emailAddressId,
          })
          setSuccess(true)
        } else {
          setError('Unable to initiate password reset. Please try again.')
        }
      } else {
        setError('Unable to initiate password reset. Please try again.')
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Failed to send reset email. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Section - Branding */}
      <div className="hidden lg:flex relative w-[504px] bg-[#2b42a3] overflow-hidden justify-center">
        <div className="absolute inset-0 bg-[#414fbf] opacity-90" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="mb-8">
              <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
                <ChevronLeft className="h-4 w-4" />
                <span>Return Home</span>
              </Link>
            </div>
            
            <div className="mb-8">
              <BrainLogo size="lg" variant="dark" showGlow={false} />
            </div>
            
            <div className="mb-6">
              <h1 className="text-[39px] font-black leading-[47px] mb-4">
                Reset Your Password
              </h1>
              <p className="text-[#bdbdbd] text-base leading-[28px]">
                Enter your email address and we&apos;ll send you a link to reset your password.
              </p>
            </div>
          </div>
        </div>
        <img
          src="/sign_in_image.png"
          alt="Sign in illustration"
          className="absolute bottom-0 left-0 right-0 w-full h-auto object-contain"
        />
      </div>

      {/* Right Section - Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
            <ChevronLeft className="h-4 w-4" />
            <span>Return Home</span>
          </Link>
          <Link to="/sign-in" className="text-sm text-slate-600">
            <span className="font-normal">Remember your password? </span>
            <span className="font-bold underline">SIGN IN</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-8 pb-0 lg:p-12 lg:pb-0 bg-[#F8FAFC]">
          <div className="w-full max-w-[460px]">
            <div className="border border-slate-200 rounded-[40px] p-6">
              <div className="rounded-[40px] p-6 -m-1">
                {/* Header */}
                <div className="mb-8">
                  <p className="text-[12.8px] font-normal text-slate-500 mb-2">RESET PASSWORD</p>
                  <h2 className="text-[25px] font-medium leading-[44px]">Forgot Your Password?</h2>
                  <p className="text-sm text-slate-600 mt-2">
                    No worries! Enter your email address and we&apos;ll send you instructions to reset your password.
                  </p>
                </div>

                {success ? (
                  <div className="space-y-6">
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <Mail className="w-8 h-8 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Check your email</h3>
                        <p className="text-sm text-slate-600">
                          We&apos;ve sent a password reset code to <strong>{email}</strong>
                        </p>
                        <p className="text-sm text-slate-600 mt-2">
                          Enter the code on the reset password page to create a new password. The code will expire in 1 hour.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-4 py-4">
                      <Link to="/reset-password">
                        <Button
                          className="w-full h-[54px] rounded-[50px] bg-[#4669ed] hover:bg-[#2b42a3] text-white font-bold text-base"
                        >
                          Enter Reset Code
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/sign-in">
                        <Button
                          variant="outline"
                          className="w-full h-[54px] rounded-[50px] border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-bold text-base"
                        >
                          Back to Sign In
                        </Button>
                      </Link>
                    </div>
                    <p className="text-center text-sm text-slate-500 mt-4">
                      Didn&apos;t receive the code?{' '}
                      <button
                        onClick={() => setSuccess(false)}
                        className="font-semibold text-[#4669ed] hover:underline"
                      >
                        Try again
                      </button>
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Email */}
                    <div className="space-y-3">
                      <Label htmlFor="forgot-email" className="text-base text-[#303030]">
                        Email Address<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="johndoe@gmail.com"
                        required
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                        {error}
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-[54px] rounded-[50px] bg-[#4669ed] hover:bg-[#2b42a3] text-white font-bold text-base mt-6"
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Code'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                )}

                {/* Sign In Link */}
                <p className="text-center text-[12.8px] text-[#212121] mt-6">
                  <span className="font-normal">Remember your password? </span>
                  <Link to="/sign-in" className="font-bold underline">
                    SIGN IN
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

